import {
  ArrowRight,
  Calendar,
  ChevronRight,
  Filter,
  Mail,
  Plus,
  TrendingUp,
  Users,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { RootState } from 'store/rootReducer';
import { getWorkspaceDataRequest } from 'store/user/user.action';
import {
  selectWorkspace,
  selectWorkspaceDataError,
  selectWorkspaceDataLoading,
} from 'store/user/user.selector';
import { ROUTES } from '../../constants';
import { getDomainsRequest } from '../../store/domain/domain.action';
import { selectDomains } from '../../store/domain/domain.selector';

const Dashboard: React.FC = () => {
  const dispatch = useDispatch();
  const domains = useSelector(selectDomains);
  const currentWorkspace = useSelector((state: RootState) =>
    selectWorkspace(state)
  );
  const workspaceDataLoading = useSelector(selectWorkspaceDataLoading);
  const workspaceDataError = useSelector(selectWorkspaceDataError);
  const hasDomains = domains && domains.length > 0;
  const [domainsFetched, setDomainsFetched] = useState(false);
  const [workspaceDataFetched, setWorkspaceDataFetched] = useState(false);

  useEffect(() => {
    // Only fetch domains once when component mounts
    if (!domainsFetched) {
      dispatch(getDomainsRequest() as any);
      setDomainsFetched(true);
    }
  }, [dispatch, domainsFetched]);

  useEffect(() => {
    // Only fetch workspace data once when component mounts and workspace data is not already available
    if (!workspaceDataFetched && !currentWorkspace && !workspaceDataLoading) {
      dispatch(getWorkspaceDataRequest() as any);
      setWorkspaceDataFetched(true);
    }
  }, [dispatch, workspaceDataFetched, currentWorkspace, workspaceDataLoading]);

  // Show loading state if workspace data is being fetched
  if (workspaceDataLoading && !currentWorkspace) {
    return (
      <div className="space-y-6 flex items-center justify-center h-[calc(100vh-100px)]">
        <div className="flex flex-col items-center justify-center">
          <h1 className="text-xl font-medium text-foreground mb-2">
            Loading workspace data...
          </h1>
          <div className="h-20 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if workspace data failed to load
  if (workspaceDataError && !currentWorkspace) {
    return (
      <div className="space-y-6 flex items-center justify-center h-[calc(100vh-100px)]">
        <div className="flex flex-col items-center justify-center">
          <h1 className="text-xl font-medium text-foreground mb-2">
            Error loading workspace data
          </h1>
          <p className="text-destructive text-sm mb-2">{workspaceDataError}</p>
          <button
            onClick={() => {
              setWorkspaceDataFetched(false);
              dispatch(getWorkspaceDataRequest() as any);
            }}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary-hover transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4 bg-background">
      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Overview of your email automation performance at a glance
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to={ROUTES.SEGMENTS}
            className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg text-foreground hover:bg-muted transition-colors font-medium text-sm"
          >
            <Filter className="w-4 h-4" />
            Create Segment
          </Link>
          <Link
            to={ROUTES.CAMPAIGNS}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary-hover transition-colors font-medium text-sm"
          >
            <Plus className="w-4 h-4" />
            New Campaign
          </Link>
        </div>
      </div>

      {!hasDomains && (
        <div className="space-y-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 text-primary border-2 border-primary rounded-full flex items-center justify-center font-bold text-sm">
                  1
                </div>
                <div>
                  <h3 className="text-md font-medium text-gray-900">
                    Domain Setup
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Configure your sending domains and DNS records for email
                    delivery
                  </p>
                </div>
              </div>
              <Link
                to={ROUTES.DOMAIN_SETUP}
                className="flex items-center gap-2 px-4 py-2 hover:bg-primary hover:text-primary-foreground border border-gray-200 rounded-lg transition-colors font-medium text-sm"
              >
                Setup Domain
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 text-primary border-2 border-primary rounded-full flex items-center justify-center font-bold text-sm">
                  2
                </div>
                <div>
                  <h3 className="text-md font-medium text-gray-900">
                    Import/Create Contact
                  </h3>
                  <p className="text-gray-600 text-sm">
                    See how we automate sending messages with a campaign
                    template
                  </p>
                </div>
              </div>
              <Link
                className="flex items-center gap-2 px-4 py-2 hover:bg-primary hover:text-primary-foreground border border-gray-200 rounded-lg transition-colors font-medium text-sm"
                to={ROUTES.CONTACTS}
              >
                Add Contacts
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 text-primary border-2 border-primary rounded-full flex items-center justify-center font-bold text-sm">
                  3
                </div>
                <div>
                  <h3 className="text-md font-medium text-gray-900">
                    Create Campaign
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Send a test email using our intuitive drag-and-drop builder
                  </p>
                </div>
              </div>
              <Link
                className="flex items-center gap-2 px-4 py-2 hover:bg-primary hover:text-primary-foreground border border-gray-200 rounded-lg transition-colors font-medium text-sm"
                to={ROUTES.CAMPAIGNS}
              >
                Create Campaign
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {hasDomains && (
        <>
          {/* Stats Cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {/* Active Users Card */}
            <div className="bg-card rounded-xl border border-border p-5">
              <div className="flex items-start justify-between">
                <div className="space-y-3">
                  <p className="text-muted-foreground text-sm font-medium">
                    Active Users
                  </p>
                  <h3 className="text-3xl font-bold text-card-foreground">
                    {(currentWorkspace.contactsCount || 0).toLocaleString()}
                  </h3>
                </div>
                <div className="p-2 rounded-lg bg-muted">
                  <Users className="w-5 h-5 text-muted-foreground" />
                </div>
              </div>
            </div>

            {/* Messages Sent Card */}
            <div className="bg-card rounded-xl border border-border p-5">
              <div className="flex items-start justify-between">
                <div className="space-y-3">
                  <p className="text-muted-foreground text-sm font-medium">
                    Messages Sent
                  </p>
                  <h3 className="text-3xl font-bold text-card-foreground">
                    {(
                      currentWorkspace.emailSentHistoryCount || 0
                    ).toLocaleString()}
                  </h3>
                </div>
                <div className="p-2 rounded-lg bg-muted">
                  <Mail className="w-5 h-5 text-muted-foreground" />
                </div>
              </div>
            </div>

            {/* Engagement Rate Card */}
            <div className="bg-card rounded-xl border border-border p-5">
              <div className="flex items-start justify-between">
                <div className="space-y-3">
                  <p className="text-muted-foreground text-sm font-medium">
                    Active Segments
                  </p>
                  <h3 className="text-3xl font-bold text-card-foreground">
                    {(currentWorkspace.segmentsCount || 0).toLocaleString()}
                  </h3>
                </div>
                <div className="p-2 rounded-lg bg-muted">
                  <TrendingUp className="w-5 h-5 text-muted-foreground" />
                </div>
              </div>
            </div>
          </div>

          {/* Recent Campaigns */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-foreground">
                  Recent Campaigns
                </h2>
                <p className="text-muted-foreground text-sm">
                  Track performance of your latest email campaigns
                </p>
              </div>
              <Link
                to="/campaigns"
                className="flex items-center gap-2 text-primary hover:bg-muted p-3 rounded-lg transition-colors font-medium text-sm"
              >
                View All
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="overflow-x-auto rounded-xl border border-border bg-card">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                      Campaign
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                      Recipients
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                      Open Rate
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                      Click Rate
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                      Sent
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {!currentWorkspace?.campaigns?.length ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-12 text-center text-muted-foreground"
                      >
                        <div className="flex flex-col items-center justify-center gap-2">
                          <Mail className="w-10 h-10 text-muted-foreground/50" />
                          <p className="text-sm font-medium">
                            No campaigns yet
                          </p>
                          <p className="text-xs max-w-sm">
                            Create your first campaign to start sending emails
                            and track performance here.
                          </p>
                          <Link
                            to="/campaigns"
                            className="mt-2 flex items-center gap-2 text-primary hover:bg-muted p-2 rounded-lg transition-colors text-sm font-medium"
                          >
                            Go to Campaigns
                            <ChevronRight className="w-4 h-4" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    currentWorkspace?.campaigns?.map((campaign: any) => (
                      <tr
                        key={campaign._id}
                        className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <Link
                            to={`/campaigns/builder/${campaign._id}`}
                            className="text-sm font-medium text-card-foreground hover:underline inline-block"
                          >
                            {campaign.name}
                          </Link>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-background text-success">
                            {campaign.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-card-foreground">
                            {(campaign.recipientsCount || 0).toLocaleString()}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-card-foreground">
                            <div className="flex items-center gap-1">
                              <div className="w-2 h-2 bg-muted-foreground rounded-full"></div>
                              <span>
                                {(campaign.openRate || 0)?.toFixed(2)}%
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-card-foreground">
                            <div className="flex items-center gap-1">
                              <div className="w-2 h-2 bg-muted-foreground rounded-full"></div>
                              <span>
                                {(campaign.clickRate || 0)?.toFixed(2)}%
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              <span>{campaign.updatedAt}</span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
